import { timestampFromDate } from '@bufbuild/protobuf/wkt';
import { Client } from '@connectrpc/connect';

import { SessionService } from './espb/session_service_pb';
import { requireFieldNotEmpty } from './grpcUtils';
import { toSyncDto } from './sync';
import { SessionId, AuthenticatorId, SyncUpdate, SyncUpdateField, ProfileId } from './types';

export default class SessionServiceClient {
  constructor(private readonly espbClient: Client<typeof SessionService>) {}

  async deleteSession(sessionId: SessionId): Promise<SyncUpdate | undefined> {
    const resp = await this.espbClient.deleteSession({ sessionId });
    const { syncUpdate } = resp;
    return syncUpdate ? toSyncDto(syncUpdate) : undefined;
  }

  async createSessionUnlock(
    sessionId: SessionId,
    authenticatorId: AuthenticatorId,
    encryptedSecret: Uint8Array,
    maxExpiresAt: Date,
  ): Promise<SyncUpdate> {
    const resp = await this.espbClient.createSessionUnlock({
      sessionId,
      authenticatorId,
      encryptedSecret,
      maxExpiresAt: timestampFromDate(maxExpiresAt),
    });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async extendSessionUnlock(lastUserActivity: Date): Promise<SyncUpdate | undefined> {
    const resp = await this.espbClient.extendSessionUnlock({
      lastUserActivity: timestampFromDate(lastUserActivity),
    });
    const { syncUpdate } = resp;
    return syncUpdate ? toSyncDto(syncUpdate) : undefined;
  }

  async deleteSessionUnlock(sessionId: SessionId, onlyPendingRequest = false): Promise<SyncUpdate> {
    const resp = await this.espbClient.deleteSessionUnlock({
      sessionId,
      onlyPendingRequest,
    });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async requestSessionUnlock(source: string): Promise<SyncUpdate> {
    const resp = await this.espbClient.requestSessionUnlock({ source });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async update(
    sessionId: SessionId,
    fieldsToUpdate: SyncUpdateField[],
    {
      clientSettings,
      deviceInfo,
      enabledProfileIds,
      unlockTimeLimitMinutes,
      fcmDeviceToken,
      apnsDeviceToken,
    }: {
      clientSettings?: string;
      deviceInfo?: string;
      enabledProfileIds?: ProfileId[];
      unlockTimeLimitMinutes?: number;
      fcmDeviceToken?: string;
      apnsDeviceToken?: string;
    },
  ) {
    const resp = await this.espbClient.update({
      sessionId,
      fieldsToUpdate,
      clientSettings,
      deviceInfo,
      enabledProfiles: enabledProfileIds ? { profileIds: enabledProfileIds } : undefined,
      unlockTimeLimitMinutes,
      fcmDeviceToken,
      apnsDeviceToken,
    });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }
}
